import { createContext, useState, useEffect } from 'react';
import FetchService from './fetch.service';
import Cookies from "js-cookie";
import localStorageService from './localStorage.service';
import { set } from 'date-fns';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loginRespData, setLoginRespData] = useState(null);
    const [companyId, setCompanyId] = useState(localStorageService.getCompanyId());
    const [companyList, setCompanyList] = useState(localStorageService.getCompanyList());
    const [companyInfo, setCompanyInfo] = useState(localStorageService.getCompanyInfo());
    const [adminUser, setAdminUser] = useState(localStorageService.getAdminUser());

    const handleCompanyChange = (id) => {
        setCompanyId(id);
        localStorageService.setCompanyId(id);
    }

    useEffect(() => {
        setCompanyInfo(localStorageService.getCompanyInfo());
    }, [companyId]);
    
    const login = async ({ username, password, captchaToken }) => {
        const reqData = { username, password, captchaToken };
        const fetchService = new FetchService();

        const response = await fetchService("/login/", {
            method: 'POST',
            cache: 'no-cache',
            mode: 'cors',
            body: JSON.stringify(reqData),
            headers: {
                'Accept': 'application/json',
                "Content-Type": 'application/json',
            },
            credentials: 'include',
        });
        const respData = await response.json();
        const loginResult = !!respData?.result;
        setLoginRespData(loginResult ? respData : null);
        let TwoFactorEnabled = true;
        if (respData?.user_data?.two_factor_auth?.two_factor_authentication_enabled) {
            if (respData?.user_data?.two_factor_auth.remember_me){
                const timestampDB = new Date(respData?.user_data?.two_factor_auth.remember_me_time).getTime();
                const currentTime = new Date().getTime();
                const timeDiff = currentTime - timestampDB;
                const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
                if (timeDiff < thirtyDaysInMs) {
                    TwoFactorEnabled = false;
                }
            }
        } else{
            TwoFactorEnabled = false;
        }
        return {
            result: respData,
            email: respData?.user_data?.email,
            TwoFactorEnabled: TwoFactorEnabled
        };
    }
    const setLocalStorageAfterLogin = (input) => {
            const respData =(input) ? input : loginRespData
            const userData = {};
            userData.user_id = respData?.user_data?.user_id;
            userData.full_name = respData?.user_data?.full_name;
            userData.company_id = respData?.user_data?.company_id;
            userData.company_name = respData?.user_data?.company_name;
            userData.email = respData?.user_data?.email;
            userData.phone_number = respData?.user_data?.phone_number;
            userData.companies_list = respData?.companies_list;
            userData.user_is_admin = respData?.user_data?.user_is_admin;
            userData.is_staff = respData?.user_data?.is_staff;
            userData.role = respData?.user_data?.role;
            localStorageService.setToken(respData?.token);
            localStorageService.setCompanyList(userData?.companies_list);
            localStorageService.setCompanyId(userData?.company_id);
            localStorageService.setUserData(userData);
            localStorageService.setAdminUser(respData?.user_data?.user_is_admin);

            const curCompany = userData?.companies_list?.find(elem => elem.id === userData?.company_id) || {};

            setCompanyId(userData?.company_id);
            setCompanyInfo(curCompany || {});
            setCompanyList(userData?.companies_list || []);
            setAdminUser(respData?.user_data?.user_is_admin);

            Cookies.set("userToken", respData?.token, { expires: 7 });
            setIsLoggedIn(true);
        return userData
    }

    const googleLogin = async (tempToken) => {
        try {
            // Make API URL dynamic based on environment
            const apiBaseUrl = window.location.hostname === 'localhost' 
                ? 'http://localhost:8000' 
                : 'https://cc-portal-backend-service-6k3dd6vtka-ez.a.run.app';
            
            const response = await fetch(`${apiBaseUrl}/profiles/auth/google-data/?temp_token=${tempToken}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Google login failed');
            }

            const respData = await response.json();
            
            if (respData.result) {
                // Store response data but don't set localStorage yet - let sendOtc handle it
                setLoginRespData(respData);
                
                // Check two-factor authentication exactly like regular login
                let TwoFactorEnabled = true;
                if (respData?.user_data?.two_factor_auth?.two_factor_authentication_enabled) {
                    if (respData?.user_data?.two_factor_auth.remember_me){
                        const timestampDB = new Date(respData?.user_data?.two_factor_auth.remember_me_time).getTime();
                        const currentTime = new Date().getTime();
                        const timeDiff = currentTime - timestampDB;
                        const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
                        if (timeDiff < thirtyDaysInMs) {
                            TwoFactorEnabled = false;
                        }
                    }
                } else{
                    TwoFactorEnabled = false;
                }
                
                // Return same structure as login() function
                return {
                    result: respData,
                    email: respData?.user_data?.email,
                    TwoFactorEnabled: TwoFactorEnabled
                };
            } else {
                throw new Error('Invalid response from Google auth API');
            }
        } catch (error) {
            console.error('Google login error:', error);
            return {
                result: false,
                error: error.message
            };
        }
    }
    const logout = async () => {
        const fetchService = new FetchService();

        const response = await fetchService("/logout/", {
            method: 'POST',
            cache: 'no-cache',
            headers: {
                'Accept': 'application/json',
                "Content-Type": 'application/json',
            },
            credentials: 'include',
        });

        await response.json();

        setIsLoggedIn(false);
        Cookies.remove("userToken");

    }

    const resetPassword = async ({ email }) => {
        const fetchService = new FetchService();
    
        const response = await fetchService("/password-reset/", {
          method: "POST",
          cache: "no-cache",
          mode: "cors",
          body: JSON.stringify({ email: email }),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
    
        const respData = await response.json();
    
        let responseResult = respData;
    
        return responseResult;
      };
    
      const passwordChange = async ({ token, password }) => {
        const fetchService = new FetchService();
    
        const response = await fetchService("/change-password/", {
          method: "POST",
          cache: "no-cache",
          mode: "cors",
          body: JSON.stringify({ password: password, token: token }),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
    
        const respData = await response.json();
    
        let responseResult = respData;
    
        return responseResult;
      };
    
    const inviteUser = async ({ email, first_name, last_name, company_id }) => {
        const fetchService = new FetchService();

        const response = await fetchService("/invite_user/", {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            body: JSON.stringify({ 
                email, 
                first_name, 
                last_name, 
                company_id 
            }),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
        });

        const respData = await response.json();
        return respData;
    };

    const setupPassword = async ({ token, password1, password2 }) => {
        const fetchService = new FetchService();

        const response = await fetchService("/setup_password/", {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            body: JSON.stringify({ token, password1, password2 }),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
        });

        const respData = await response.json();
        return respData;
    };

    const checkToken = async (token, email = null) => {
        const fetchService = new FetchService();
        
        // Build URL with email query parameter if provided
        let url = `/setup_password/check-token/${token}/`;
        if (email) {
            url += `?email=${encodeURIComponent(email)}`;
        }
    
        const response = await fetchService(url, {
            method: "GET",
            cache: "no-cache",
            mode: "cors",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
        });
    
        if (!response.ok) {
            throw new Error('Token validation failed');
        }
    
        const respData = await response.json();
        return respData;
    };

    const resendInvitation = async ({ email }) => {
        const fetchService = new FetchService();
    
        const response = await fetchService("/setup_password/resend-invitation/", {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            body: JSON.stringify({ email }),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
        });
    
        const respData = await response.json();
        return respData;
    };

    const deleteUser = async ({ email, company_id }) => {
        const fetchService = new FetchService();
        const currentUserEmail = localStorageService.getUserData()?.email;
        const isSelfDelete = email === currentUserEmail;

        try {
            const response = await deleteUserRequest({ email, company_id });
            
            if (response.result && isSelfDelete) {
                // First logout
                await logout();
                // Then clear any remaining data
                localStorageService.clearLocalStorage();
                // Finally redirect to login
                window.location.replace('/login');
                return response;
            }
            
            return response;
        } catch (error) {
            throw error;
        }
    };

    const deleteUserRequest = async ({ email, company_id }) => {
        const fetchService = new FetchService();
        const response = await fetchService("/delete_user/", {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            body: JSON.stringify({ email, company_id }),
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
        });

        return await response.json();
    };

    // Check if the user is already logged in when the app loads
    useEffect(() => {
        // Make an Axios GET request to check the user's login status and update `isLoggedIn` accordingly.
        const token = localStorageService.getToken();
        setIsLoggedIn(token !== null);
    }, []);

    const contextValue = {
        isLoggedIn,
        login,
        googleLogin,
        setLocalStorageAfterLogin,
        logout,
        companyId,
        companyInfo,
        companyList,
        handleCompanyChange,
        resetPassword,
        passwordChange,
        inviteUser,
        setupPassword,
        checkToken,
        resendInvitation,
        deleteUser,
        adminUser
    }

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
